import { differenceInDays, format, isDate } from 'date-fns'
import { get } from 'lodash'
import { isEmpty } from 'lodash/fp'
import i18n from '../../../i18n'
import { CountryCode } from '../../../i18n/countryCodes'
import { ICountry } from '../../../models/Country'
import { ICurrency } from '../../../models/Currency'
import { IAdditionalService, IEvent } from '../../../models/Event'
import { labels } from '../../../models/Event/eventConfig'
import { ICustomReservation, IProfile } from '../../../models/Object'
import { IRoom } from '../../../models/Room'
import { parseSeparatorDelimitedTime } from '../../../utils/Date'
import { formatMoney, formatNumber } from '../../../utils/Money'

interface IWithDate {
  format: string
  formatDate: string
  formatHours: string
}

interface IWithPreferred {
  preferredArrival: string
  preferredDeparture: string
}

interface IWithCurrency {
  currencies: ICurrency[]
}

interface IWithCountry {
  countries: ICountry[]
}

interface IWithPreferences {
  labels: ICustomReservation
}

interface IWithLanguage {
  language: CountryCode
}

export type ITags = ITagsForEvents &
  ITagsForRooms &
  ITagsForEventsWithPreferences

export interface ITagsForEvents {
  '#Source.of.booking.origin#': ITag<IEvent, string>
  '#From#': ITag<IEvent & IWithDate, string>
  '#To#': ITag<IEvent & IWithDate, string>
  '#Arrival#': ITag<IEvent & IWithDate & IWithPreferred, string>
  '#Departure#': ITag<IEvent & IWithDate & IWithPreferred, string>
  '#No.of.days#': ITag<IEvent, string>
  '#Adults#': ITag<IEvent, number>
  '#Children#': ITag<IEvent, number>
  '#Price.per.day#': ITag<IEvent, string>
  '#Final.price#': ITag<IEvent, string>
  '#Payment.on.place#': ITag<IEvent, string>
  '#Deposit#': ITag<IEvent, string>
  '#Local.tax#': ITag<IEvent, string>
  '#Date.to.pay#': ITag<IEvent & IWithDate, string>
  '#Date.to.pay.after.start#': ITag<IEvent & IWithDate, string>
  '#Notes#': ITag<IEvent, string>
  '#Booking.no#': ITag<IEvent, string>
  '#Registration#': ITag<IEvent & IWithLanguage, string>
  '#Door.code#': ITag<IEvent, string>
  '#Guest.name#': ITag<IEvent, string>
  '#Guest.phone#': ITag<IEvent, string>
  '#Guest.email#': ITag<IEvent, string>
  '#Board#': ITag<IEvent & IWithLanguage, string>
  '#Stay.deposit#': ITag<IEvent, string>
  '#Advance#': ITag<IEvent, string>
  '#Provenance#': ITag<IEvent, string>
}

export interface ITagsForRooms {
  '#Name.of.room#': ITag<IRoom, string>
  '#Type.of.room#': ITag<IRoom & IWithLanguage, string>
}

export interface ITagsForEventsAdditional {
  '#Age.of.children#': ITag<IEvent, string>
  '#Guest.country#': ITag<IEvent & IWithCountry, string>
  '#Date.of.prepayment#': ITag<IEvent & IWithDate, string>
  '#Guest.company.name#': ITag<IEvent, string>
  '#Guest.company.street#': ITag<IEvent, string>
  '#Guest.company.city#': ITag<IEvent, string>
  '#Guest.company.post.code#': ITag<IEvent, string>
  '#Guest.company.country#': ITag<IEvent & IWithCountry, string>
  '#Guest.company.vat.id#': ITag<IEvent, string>
  '#Guest.passport#': ITag<IEvent, string>
  '#Prepayment#': ITag<IEvent, string>
  '#Guest.id.card#': ITag<IEvent, string>
  '#Guest.street#': ITag<IEvent, string>
  '#Date.of.payment.on.place#': ITag<IEvent & IWithDate, string>
  '#Guest.house.number#': ITag<IEvent, string>
  '#Guest.apartment.number#': ITag<IEvent, string>
  '#Guest.city#': ITag<IEvent, string>
  '#Guest.post.code#': ITag<IEvent, string>
  '#Booking.added#': ITag<IEvent & IWithDate, string>
  '#Additional.services#': ITag<IEvent, string>
}

export interface ITagsForProfile {
  '#Company.name#': ITag<IProfile, string>
  '#Company.country#': ITag<IProfile & IWithCountry, string>
  '#Company.post.code#': ITag<IProfile, string>
  '#Company.city#': ITag<IProfile, string>
  '#Company.street#': ITag<IProfile, string>
  '#Company.vat.id#': ITag<IProfile, string>
  '#Company.iban#': ITag<IProfile, string>
  '#Company.swift#': ITag<IProfile, string>
  '#Company.currency#': ITag<IProfile & IWithCurrency, string>
  '#Property.city#': ITag<IProfile, string>
  '#Property.country#': ITag<IProfile & IWithCountry, string>
  '#Property.street#': ITag<IProfile, string>
  '#Property.post.code#': ITag<IProfile, string>
  '#Property.phone#': ITag<IProfile, string>
  '#Property.email#': ITag<IProfile, string>
  '#Property.website#': ITag<IProfile, string>
  '#Property.facebook#': ITag<IProfile, string>
  '#Property.name#': ITag<IProfile, string>
}

export interface ITagsForEventsWithPreferences {
  '#Booking.status#': ITag<
    IEvent & IWithLanguage & { settings: IWithPreferences },
    string
  >
}

export interface ITag<T = {}, V = string> {
  value: string

  map(values: T): V
}

function makeTag<T = {}, V = string>(
  value: string,
  cb: (v: T) => V,
): ITag<T, V> {
  return { map: cb, value }
}

/* eslint-disable */
const tagsForEvents: ITagsForEvents = {
  // Hidden from Personalizacja picker (SettingsForm.getTags) — BED-40785; keep for saved label patterns.
  '#Source.of.booking.origin#': makeTag('BED_7482_source_icon', () => ''),
  '#From#': makeTag('reservation_from', v => {
    return format(v.startDate, v.formatDate)
  }),
  '#To#': makeTag('reservation_to', v => {
    return !v.finishDate ? '' : format(v.finishDate, v.formatDate)
  }),
  '#Arrival#': makeTag('arrival', v => {
    if (!v.arrivalTime) {
      return ''
    }

    return isDate(v.arrivalTime)
      ? format(v.arrivalTime, v.formatHours)
      : format(
          parseSeparatorDelimitedTime(v.arrivalTime as string),
          v.formatHours,
        )
  }),
  '#Departure#': makeTag('departure', v => {
    if (!v.departureTime) {
      return ''
    }

    return isDate(v.departureTime)
      ? format(v.departureTime, v.formatHours)
      : format(
          parseSeparatorDelimitedTime(v.departureTime as string),
          v.formatHours,
        )
  }),
  '#No.of.days#': makeTag('reservation_number_of_days', v =>
    v.finishDate && v.startDate
      ? differenceInDays(v.finishDate, v.startDate).toString()
      : '',
  ),
  '#Adults#': makeTag('reservation_adults', v => v.adults),
  '#Children#': makeTag('reservation_children', v => v.kids),
  '#Price.per.day#': makeTag('reservation_price_per_day', v => {
    return formatMoney(v.pricePerDay) !== '0,00'
      ? formatMoney(v.pricePerDay)
      : ''
  }),
  '#Final.price#': makeTag('reservation_price_final', v =>
    formatMoney(v.price) !== '0,00' ? formatMoney(v.price) : '',
  ),
  '#Payment.on.place#': makeTag('reservation_pay_on_place', v => {
    return formatMoney(v.paymentOnPlace) !== '0,00'
      ? formatMoney(v.paymentOnPlace)
      : ''
  }),
  '#Deposit#': makeTag('reservation_deposit', v =>
    formatMoney(v.deposit) !== '0,00' ? formatMoney(v.deposit) : '',
  ),
  '#Local.tax#': makeTag('reservation_local_tax', v => {
    if (v.localTaxIncluded) return '0'

    return formatMoney(v.localTax) !== '0,00' ? formatMoney(v.localTax) : ''
  }),
  '#Date.to.pay#': makeTag('z677_waiting_for_payment', v =>
    v.dateToPay ? format(v.dateToPay, v.formatDate) : '',
  ),
  '#Date.to.pay.after.start#': makeTag('z677_waiting_for_payment_time', v =>
    v.dateToPayAfterStart ? format(v.dateToPayAfterStart, v.formatDate) : '',
  ),
  '#Notes#': makeTag('reservation_note', v => v.notes || ''),
  '#Booking.no#': makeTag('reservation_reservation_id', v => v.id || ''),
  '#Registration#': makeTag('reservation_registration', v => {
    const dict = {
      '1': 'checked_in',
      '2': 'checked_out',
    }

    return v.registration && v.registration !== '0'
      ? i18n.t(`reservation_registration_${dict[v.registration]}`, {
          lng: v.language,
        })
      : ''
  }),
  '#Door.code#': makeTag('door_code', v => v.doorCode || ''),
  '#Guest.name#': makeTag('reservation_person', v => get(v, 'guest.name', '')),
  '#Guest.phone#': makeTag('reservation_phone', v => get(v, 'guest.phone', '')),
  '#Guest.email#': makeTag('email', v => get(v, 'guest.email', '')),
  '#Board#': makeTag('reservation_services', v =>
    v.service
      ? i18n.t(`reservation_services_type_${v.service.toLowerCase()}`, {
          lng: v.language,
        })
      : '',
  ),
  '#Stay.deposit#': makeTag('pre_payment_form_down', v =>
    v.provision &&
    String(v.prepaidType) === '0' &&
    formatMoney(v.provision) !== '0,00'
      ? formatMoney(v.provision)
      : '',
  ),
  '#Advance#': makeTag('reservation_advance_payment', v =>
    v.provision &&
    String(v.prepaidType) === '1' &&
    formatMoney(v.provision) !== '0,00'
      ? formatMoney(v.provision)
      : '',
  ),
  '#Provenance#': makeTag(
    'pref_reservation_show_provenance',
    v => v.provenance || '',
  ),
}

const tagsForEventsAdditional: ITagsForEventsAdditional = {
  '#Additional.services#': makeTag('z2736_label_additional_services', v =>
    !isEmpty(v.additionalServices)
      ? formatNumber(
          v.additionalServices.reduce(
            (acc: number, cur: IAdditionalService) => {
              return acc + Number(cur.price || 0)
            },
            0,
          ),
        )
      : '',
  ),
  '#Age.of.children#': makeTag('reservation_kids_age', v =>
    v.kidsAge && !v.kidsAge.every(ka => ka === '0') ? v.kidsAge.join(', ') : '',
  ),
  '#Booking.added#': makeTag('todo_added_text', v =>
    v.createdAt ? format(v.createdAt, v.formatDate) : '',
  ),
  '#Date.of.payment.on.place#': makeTag('date_of_payment', v =>
    v.paymentOnPlaceDate ? format(v.paymentOnPlaceDate, v.formatDate) : '',
  ),
  '#Date.of.prepayment#': makeTag('date_of_payment', v =>
    v.provisionDate ? format(v.provisionDate, v.formatDate) : '',
  ),
  '#Guest.apartment.number#': makeTag('reservation_person_apartment', v =>
    get(v, 'guest.apartmentNumber', ''),
  ),
  '#Guest.city#': makeTag('reservation_person_town', v =>
    get(v, 'guest.city', ''),
  ),
  '#Guest.company.city#': makeTag('reservation_person_town', v =>
    get(v, 'company.city', ''),
  ),
  '#Guest.company.country#': makeTag('profile_country', v =>
    v.company && v.company.country
      ? get(v.countries.find(c => c.code === v.company.country), 'name', '')
      : '',
  ),
  '#Guest.company.name#': makeTag('reservation_person', v =>
    get(v, 'company.name', ''),
  ),
  '#Guest.company.post.code#': makeTag('reservation_person_post_code', v =>
    get(v, 'company.postCode', ''),
  ),
  '#Guest.company.street#': makeTag('reservation_person_street', v =>
    get(v, 'company.street', ''),
  ),
  '#Guest.company.vat.id#': makeTag('profile_vat_number', v =>
    get(v, 'company.vatId', ''),
  ),
  '#Guest.country#': makeTag('profile_country', v =>
    v.guest && v.guest.country
      ? get(v.countries.find(c => c.code === v.guest.country), 'name', '')
      : '',
  ),
  '#Guest.house.number#': makeTag('reservation_person_house', v =>
    get(v, 'guest.houseNumber', ''),
  ),
  '#Guest.id.card#': makeTag('reservation_person_identity_card', v =>
    get(v, 'guest.idCard', ''),
  ),
  '#Guest.passport#': makeTag('reservation_person_passport', v =>
    get(v, 'guest.passport', ''),
  ),
  '#Guest.post.code#': makeTag('reservation_person_post_code', v =>
    get(v, 'guest.postCode', ''),
  ),
  '#Guest.street#': makeTag('reservation_person_street', v =>
    get(v, 'guest.street', ''),
  ),
  '#Prepayment#': makeTag('BBO_988_prepayment', v =>
    v.provision
      ? formatMoney(v.provision) !== '0,00'
        ? formatMoney(v.provision)
        : ''
      : '',
  ),
}

const tagsForRooms: ITagsForRooms = {
  '#Name.of.room#': makeTag('room_name', v => v.name || ''),
  '#Type.of.room#': makeTag('tag_type_of_room', v =>
    v.type
      ? i18n.t(`room_type_${v.type}`, {
          lng: v.language,
        })
      : '',
  ),
}

const tagsForProfile: ITagsForProfile = {
  '#Company.city#': makeTag('profile_city', v => v.companyCity),
  '#Company.country#': makeTag('profile_country', v =>
    get(v.countries.find(c => Number(c.id) === v.companyCountry), 'name', ''),
  ),
  '#Company.currency#': makeTag('profile_currency', v =>
    get(v.currencies.find(c => Number(c.id) === v.idCurrency), 'name', ''),
  ),
  '#Company.iban#': makeTag('z677_iban', v => v.accountNumber),
  '#Company.name#': makeTag('company_name', v => v.companyName),
  '#Company.post.code#': makeTag(
    'reservation_person_post_code ',
    v => v.companyPostCode,
  ),
  '#Company.street#': makeTag(
    'reservation_person_street',
    v => v.companyStreet,
  ),
  '#Company.swift#': makeTag('z677_swift', v => v.swift),
  '#Company.vat.id#': makeTag('profile_vat_number', v => v.companyVatNumber),
  '#Property.city#': makeTag('city', v => v.city),
  '#Property.country#': makeTag('profile_country', v =>
    get(v.countries.find(c => Number(c.id) === v.idCountry), 'name', ''),
  ),
  '#Property.email#': makeTag('email', v => v.email),
  '#Property.facebook#': makeTag('z677_facebook', v => v.facebook),
  '#Property.name#': makeTag('name', v => v.name),
  '#Property.phone#': makeTag('reservation_phone', v => v.phone),
  '#Property.post.code#': makeTag(
    'reservation_person_post_code',
    v => v.postCode,
  ),
  '#Property.street#': makeTag('reservation_person_street', v => v.street),
  '#Property.website#': makeTag('contact_label_website', v => v.www),
}

const tagsForEventsWithPreferences: ITagsForEventsWithPreferences = {
  '#Booking.status#': makeTag('reservation_stage', function(v) {
    return (
      v.settings.labels[v.stage] ||
      i18n.t(
        labels.get(
          String(v.stage) === '3' && String(v.prepaidType) === '0'
            ? '3-2'
            : v.stage,
        ),
        {
          lng: v.language,
        },
      )
    )
  }),
}
/* eslint-enable */

export default {
  ...tagsForEvents,
  ...tagsForRooms,
  ...tagsForEventsAdditional,
  ...tagsForProfile,
  ...tagsForEventsWithPreferences,
} as ITags
export {
  tagsForEvents,
  tagsForProfile,
  tagsForRooms,
  tagsForEventsWithPreferences,
  tagsForEventsAdditional,
}
